// import React, { ReactNode, useContext, useState } from 'react';
import React, { ReactNode, useState } from 'react';
import styled from 'styled-components';

import { ICON_TYPES } from '../../../shared/constants';
import { IParentPage } from '../../../types/shared';
import LinkIcon from './shared/LinkIcon';
import { MenuItemButton, MenuItemLink } from './shared/navigation.styles';
import NavigationCancelButton from './shared/NavigationCancelButton';
import { INavigationItem } from './shared/types';

import ChevronWrapper from '../../atoms/icons/ChevronWrapper';

import { DEQ_TYPES } from '../../../utils/deq';
import { getPositionString } from '../../../utils/deq/miscellaneousTrackingHelpers';
import { getInternalLink } from '../../../utils/generic';

import { useNavigation } from './NavigationContextProvider';

interface Props {
  activeTopItem: INavigationItem;
  onRequestCloseHandler: () => void;
}

const MobileDropdownMenu = ({
  activeTopItem,
  onRequestCloseHandler,
}: Props) => {
  const [activeLevelOneItem, setActiveLevelOneItem] =
    useState<INavigationItem>();
  const [activeLevelTwoItem, setActiveLevelTwoItem] =
    useState<INavigationItem>();
  const [activeLevelThreeItem, setActiveLevelThreeItem] =
    useState<INavigationItem>();

  const { positions, setPosition } = useNavigation();

  const updatePosition = (index: number, newPosition: number) => {
    setPosition(index, newPosition);
  };

  const setActiveItem = (level: number, item: INavigationItem) => {
    if (level === 1) {
      setActiveLevelOneItem(item === activeLevelOneItem ? undefined : item);
    }
    if (level === 2) {
      setActiveLevelTwoItem(item === activeLevelTwoItem ? undefined : item);
    }
    if (level === 3) {
      setActiveLevelThreeItem(item === activeLevelThreeItem ? undefined : item);
    }
  };

  const renderItems = (
    level: number,
    subitems: INavigationItem[],
    parent?: string
  ): ReactNode => {
    return subitems.map((item, key) => {
      return !item.subitems ? (
        <MenuItemLink
          key={`${level}-${key}-${item.displayName}`}
          href={
            item.externalPage ||
            `/${getInternalLink(item?.page as IParentPage)}`
          }
          target={item.externalPage ? '_blank' : '_self'}
          onClick={() => updatePosition(level, key)}
          level={level}
          active={
            activeLevelOneItem === item ||
            activeLevelTwoItem === item ||
            activeLevelThreeItem === item
          }
          data-testid={`level-${level}-link`}
          data-element-type={DEQ_TYPES.btnMenu}
          data-component-type={DEQ_TYPES.mainNavigation}
          data-element-label={item.displayName.toLowerCase()}
          data-element-hierarchy={`${parent ? `${parent}|` : ''}${
            item.displayName
          }`.toLowerCase()}
          data-element-target={
            item.externalPage || getInternalLink(item?.page as IParentPage)
          }
          data-element-position={getPositionString(level, key, positions)}
          isMobile
        >
          {level > 1 ? (
            <LinkIcon
              iconType={
                item.externalPage ? ICON_TYPES.external : ICON_TYPES.arrow
              }
            />
          ) : undefined}
          {item.displayName}
        </MenuItemLink>
      ) : (
        <TopBarItemButtonWrapper
          key={`wrapper-${level}-${key}-${item.displayName}`}
        >
          <MenuItemButton
            key={`${level}-${key}-${item.displayName}`}
            onClick={() => {
              updatePosition(level, key);
              setActiveItem(level, item);
            }}
            level={level}
            active={
              activeLevelOneItem === item ||
              activeLevelTwoItem === item ||
              activeLevelThreeItem === item
            }
            data-testid={`level-${level}-button`}
            data-element-type={DEQ_TYPES.btnMenu}
            data-component-type={DEQ_TYPES.mainNavigation}
            data-element-label={item.displayName.toLowerCase()}
            data-element-hierarchy={`${parent ? `${parent}|` : ''}${
              item.displayName
            }`.toLowerCase()}
            data-element-position={getPositionString(level, key, positions)}
            isMobile
          >
            <span>{item.displayName}</span>
            <ChevronWrapper
              isActive={
                activeLevelOneItem === item ||
                activeLevelTwoItem === item ||
                activeLevelThreeItem === item
              }
            />
          </MenuItemButton>
          {activeLevelOneItem === item ||
          activeLevelTwoItem === item ||
          activeLevelThreeItem === item
            ? renderItems(
                level + 1,
                item.subitems as INavigationItem[],
                `${
                  parent ? `${parent} | ${item.displayName}` : item.displayName
                }`
              )
            : undefined}
        </TopBarItemButtonWrapper>
      );
    });
  };

  return (
    <MobileDropdownContainer
      key="mobile-dropdown"
      data-testid="mobile-dropdown-container"
    >
      <NavigationCancelButtonWrapper>
        <NavigationCancelButton
          onClickHandler={() => onRequestCloseHandler()}
        />
      </NavigationCancelButtonWrapper>
      {renderItems(1, activeTopItem.subitems as INavigationItem[])}
    </MobileDropdownContainer>
  );
};

export default MobileDropdownMenu;

const MobileDropdownContainer = styled.div`
  font: inherit;
  height: calc(100vh - 112px);
  margin-top: 38px;
  overflow-y: auto;
  width: 100%;
`;

const TopBarItemButtonWrapper = styled.div`
  width: inherit;
`;

const NavigationCancelButtonWrapper = styled.div`
  position: absolute;
  right: 2px;
  top: 0px;
`;
